import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { AuthProvider, useAuth } from './auth'
import { AppShell } from './components/AppShell'
import { Login } from './pages/Login'
import { Dashboard } from './pages/Dashboard'
import { Findings } from './pages/Findings'
import { FindingDetail } from './pages/FindingDetail'
import { Engagements } from './pages/Engagements'
import { Documents } from './pages/Documents'
import { AuditTrail } from './pages/AuditTrail'
import { Billing } from './pages/Billing'
import { Payments } from './pages/Payments'
import { DSAR } from './pages/DSAR'
import { Vendors } from './pages/Vendors'
import { Inventory } from './pages/Inventory'
import { Contracts } from './pages/Contracts'
import { Ownership } from './pages/Ownership'
import { AML } from './pages/AML'
import { ERP } from './pages/ERP'
import { Renewals } from './pages/Renewals'
import { Archives } from './pages/Archives'
import { Guardrails } from './pages/Guardrails'
import { Usage } from './pages/Usage'
import { FX } from './pages/FX'
import { Jobs } from './pages/Jobs'
import { Health } from './pages/Health'
import { Rules } from './pages/Rules'
import { Payroll } from './pages/Payroll'
import { Expenses } from './pages/Expenses'
import { Cards } from './pages/Cards'
import { Network } from './pages/Network'
import { Masking } from './pages/Masking'
import { Schema } from './pages/Schema'
import { PlanChanges } from './pages/PlanChanges'
import { Partners } from './pages/Partners'
import { Pricing } from './pages/Pricing'
import { FieldCrypto } from './pages/FieldCrypto'
import { Incidents } from './pages/Incidents'
import { Residency } from './pages/Residency'
import { Licenses } from './pages/Licenses'
import { Royalties } from './pages/Royalties'
import { AccessFence } from './pages/AccessFence'
import { Vault } from './pages/Vault'
import { Locks } from './pages/Locks'
import { SharedLimits } from './pages/SharedLimits'
import { SIEM } from './pages/SIEM'
import { Traces } from './pages/Traces'
import { Live } from './pages/Live'
import { Projections } from './pages/Projections'
import { Provisioning } from './pages/Provisioning'
import { Analytics } from './pages/Analytics'
import { Cache as CachePage } from './pages/Cache'
import { AIDrafts } from './pages/AIDrafts'
import { SLACredits } from './pages/SLACredits'
import { Forecast } from './pages/Forecast'
import { Breakers } from './pages/Breakers'
import { Vulnerabilities } from './pages/Vulnerabilities'
import { PatchManagement } from './pages/PatchManagement'
import { ThreatHunting } from './pages/ThreatHunting'
import { VaultAccess } from './pages/VaultAccess'
import { Compliance } from './pages/Compliance'
import { Narratives } from './pages/Narratives'
import { UEBA } from './pages/UEBA'
import { AnomalyAlerts } from './pages/AnomalyAlerts'
import { LLMCache } from './pages/LLMCache'
import { Consensus } from './pages/Consensus'
import { Streams } from './pages/Streams'
import { Reconciliation } from './pages/Reconciliation'
import { ComplianceAI } from './pages/ComplianceAI'
import { Collusion } from './pages/Collusion'
import { AdaptiveRisk } from './pages/AdaptiveRisk'
import { NotifyEngine } from './pages/NotifyEngine'
import { Remediation } from './pages/Remediation'
import { FlagEngine } from './pages/FlagEngine'
import { Inference } from './pages/Inference'
import { ThreatIntel } from './pages/ThreatIntel'
import { ModelRegistry } from './pages/ModelRegistry'
import { Sagas } from './pages/Sagas'
import { Graph as GraphPage } from './pages/Graph'
import { LoadTest } from './pages/LoadTest'
import { Simulation } from './pages/Simulation'
import { Perf } from './pages/Perf'
import { ZeroTrust } from './pages/ZeroTrust'
import { IPLists } from './pages/IPLists'
import { Thresholds } from './pages/Thresholds'
import { Retraining } from './pages/Retraining'
import { Clustering } from './pages/Clustering'
import { CSR } from './pages/CSR'
import { Franchise } from './pages/Franchise'
import { Agents } from './pages/Agents'
import { QueryCache } from './pages/QueryCache'
import { Discovery } from './pages/Discovery'
import { Biometric } from './pages/Biometric'
import { Partitioning } from './pages/Partitioning'
import { WebSockets } from './pages/WebSockets'
import { PaymentGateway } from './pages/PaymentGateway'
import { EventStore } from './pages/EventStore'
import { Resources } from './pages/Resources'
import { EdgeAudit } from './pages/EdgeAudit'
import { AIMaster } from './pages/AIMaster'
import { Replication } from './pages/Replication'
import { Failover } from './pages/Failover'
import { Orchestration } from './pages/Orchestration'
import { Cluster as ClusterPage } from './pages/Cluster'
import { Scenarios } from './pages/Scenarios'
import { Readiness } from './pages/Readiness'
import { Autoscale } from './pages/Autoscale'
import { CDN } from './pages/CDN'
import { Enforcement } from './pages/Enforcement'
import { Geo } from './pages/Geo'
import { Multicloud } from './pages/Multicloud'
import { Rollout } from './pages/Rollout'
import { LogicalReplication } from './pages/LogicalReplication'
import { DRControl } from './pages/DRControl'
import { Voting } from './pages/Voting'
import { Release } from './pages/Release'
import { Purge } from './pages/Purge'
import { WebhookDLQ } from './pages/WebhookDLQ'
import { Soak } from './pages/Soak'
import { Cases } from './pages/Cases'
import { Security } from './pages/Security'
import { CaseDetail } from './pages/CaseDetail'
import { Board } from './pages/Board'
import { Clients } from './pages/Clients'
import { Coverage } from './pages/Coverage'
import { ApiKeys } from './pages/ApiKeys'
import { Support } from './pages/Support'
import { FeatureFlags } from './pages/FeatureFlags'
import { TrustCenter } from './pages/TrustCenter'
import { Notifications } from './pages/Notifications'
import { Search } from './pages/Search'
import { WhistlePublic } from './pages/WhistlePublic'
import { WhistleTriage } from './pages/WhistleTriage'
import { Roles } from './pages/Roles'

function Protected({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth()
  const location = useLocation()
  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: 'var(--color-ivory-dim)' }}>กำลังโหลด…</div>
  }
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <AppShell>{children}</AppShell>
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/whistle" element={<WhistlePublic />} />
          <Route path="/trust" element={<TrustCenter />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/findings" element={<Protected><Findings /></Protected>} />
          <Route path="/cases" element={<Protected><Cases /></Protected>} />
          <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
          <Route path="/findings/:id" element={<Protected><FindingDetail /></Protected>} />
          <Route path="/board" element={<Protected><Board /></Protected>} />
          <Route path="/search" element={<Protected><Search /></Protected>} />
          <Route path="/whistle/triage" element={<Protected><WhistleTriage /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/coverage" element={<Protected><Coverage /></Protected>} />
          <Route path="/api-keys" element={<Protected><ApiKeys /></Protected>} />
          <Route path="/support" element={<Protected><Support /></Protected>} />
          <Route path="/feature-flags" element={<Protected><FeatureFlags /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/engagements" element={<Protected><Engagements /></Protected>} />
          <Route path="/documents" element={<Protected><Documents /></Protected>} />
          <Route path="/audit-trail" element={<Protected><AuditTrail /></Protected>} />
          <Route path="/billing" element={<Protected><Billing /></Protected>} />
          <Route path="/payments" element={<Protected><Payments /></Protected>} />
          <Route path="/dsar" element={<Protected><DSAR /></Protected>} />
          <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
          <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
          <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
          <Route path="/ownership" element={<Protected><Ownership /></Protected>} />
          <Route path="/aml" element={<Protected><AML /></Protected>} />
          <Route path="/erp" element={<Protected><ERP /></Protected>} />
          <Route path="/renewals" element={<Protected><Renewals /></Protected>} />
          <Route path="/archives" element={<Protected><Archives /></Protected>} />
          <Route path="/guardrails" element={<Protected><Guardrails /></Protected>} />
          <Route path="/usage" element={<Protected><Usage /></Protected>} />
          <Route path="/fx" element={<Protected><FX /></Protected>} />
          <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
          <Route path="/health" element={<Protected><Health /></Protected>} />
          <Route path="/rules" element={<Protected><Rules /></Protected>} />
          <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
          <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
          <Route path="/cards" element={<Protected><Cards /></Protected>} />
          <Route path="/network" element={<Protected><Network /></Protected>} />
          <Route path="/masking" element={<Protected><Masking /></Protected>} />
          <Route path="/schema" element={<Protected><Schema /></Protected>} />
          <Route path="/billing/plan-changes" element={<Protected><PlanChanges /></Protected>} />
          <Route path="/partners" element={<Protected><Partners /></Protected>} />
          <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
          <Route path="/royalties" element={<Protected><Royalties /></Protected>} />
          <Route path="/field-crypto" element={<Protected><FieldCrypto /></Protected>} />
          <Route path="/incidents" element={<Protected><Incidents /></Protected>} />
          <Route path="/residency" element={<Protected><Residency /></Protected>} />
          <Route path="/licenses" element={<Protected><Licenses /></Protected>} />
          <Route path="/access-fences" element={<Protected><AccessFence /></Protected>} />
          <Route path="/vault" element={<Protected><Vault /></Protected>} />
          <Route path="/locks" element={<Protected><Locks /></Protected>} />
          <Route path="/shared-limits" element={<Protected><SharedLimits /></Protected>} />
          <Route path="/siem" element={<Protected><SIEM /></Protected>} />
          <Route path="/traces" element={<Protected><Traces /></Protected>} />
          <Route path="/live" element={<Protected><Live /></Protected>} />
          <Route path="/projections" element={<Protected><Projections /></Protected>} />
          <Route path="/provisioning" element={<Protected><Provisioning /></Protected>} />
          <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
          <Route path="/cache" element={<Protected><CachePage /></Protected>} />
          <Route path="/ai-drafts" element={<Protected><AIDrafts /></Protected>} />
          <Route path="/sla" element={<Protected><SLACredits /></Protected>} />
          <Route path="/forecast" element={<Protected><Forecast /></Protected>} />
          <Route path="/breakers" element={<Protected><Breakers /></Protected>} />
          <Route path="/vulnerabilities" element={<Protected><Vulnerabilities /></Protected>} />
          <Route path="/patches" element={<Protected><PatchManagement /></Protected>} />
          <Route path="/threat-hunting" element={<Protected><ThreatHunting /></Protected>} />
          <Route path="/vault-access" element={<Protected><VaultAccess /></Protected>} />
          <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
          <Route path="/narratives" element={<Protected><Narratives /></Protected>} />
          <Route path="/ueba" element={<Protected><UEBA /></Protected>} />
          <Route path="/anomalies" element={<Protected><AnomalyAlerts /></Protected>} />
          <Route path="/llm-cache" element={<Protected><LLMCache /></Protected>} />
          <Route path="/consensus" element={<Protected><Consensus /></Protected>} />
          <Route path="/streams" element={<Protected><Streams /></Protected>} />
          <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
          <Route path="/compliance-ai" element={<Protected><ComplianceAI /></Protected>} />
          <Route path="/collusion" element={<Protected><Collusion /></Protected>} />
          <Route path="/adaptive-risk" element={<Protected><AdaptiveRisk /></Protected>} />
          <Route path="/notify-engine" element={<Protected><NotifyEngine /></Protected>} />
          <Route path="/remediation" element={<Protected><Remediation /></Protected>} />
          <Route path="/flags-engine" element={<Protected><FlagEngine /></Protected>} />
          <Route path="/inference" element={<Protected><Inference /></Protected>} />
          <Route path="/threat-intel" element={<Protected><ThreatIntel /></Protected>} />
          <Route path="/model-registry" element={<Protected><ModelRegistry /></Protected>} />
          <Route path="/sagas" element={<Protected><Sagas /></Protected>} />
          <Route path="/graph" element={<Protected><GraphPage /></Protected>} />
          <Route path="/load-test" element={<Protected><LoadTest /></Protected>} />
          <Route path="/simulation" element={<Protected><Simulation /></Protected>} />
          <Route path="/perf" element={<Protected><Perf /></Protected>} />
          <Route path="/zero-trust" element={<Protected><ZeroTrust /></Protected>} />
          <Route path="/ip-lists" element={<Protected><IPLists /></Protected>} />
          <Route path="/thresholds" element={<Protected><Thresholds /></Protected>} />
          <Route path="/retraining" element={<Protected><Retraining /></Protected>} />
          <Route path="/clustering" element={<Protected><Clustering /></Protected>} />
          <Route path="/csr" element={<Protected><CSR /></Protected>} />
          <Route path="/franchise" element={<Protected><Franchise /></Protected>} />
          <Route path="/agents" element={<Protected><Agents /></Protected>} />
          <Route path="/query-cache" element={<Protected><QueryCache /></Protected>} />
          <Route path="/discovery" element={<Protected><Discovery /></Protected>} />
          <Route path="/biometric" element={<Protected><Biometric /></Protected>} />
          <Route path="/partitioning" element={<Protected><Partitioning /></Protected>} />
          <Route path="/websockets" element={<Protected><WebSockets /></Protected>} />
          <Route path="/payment-gateway" element={<Protected><PaymentGateway /></Protected>} />
          <Route path="/event-store" element={<Protected><EventStore /></Protected>} />
          <Route path="/resources" element={<Protected><Resources /></Protected>} />
          <Route path="/edge-audit" element={<Protected><EdgeAudit /></Protected>} />
          <Route path="/ai-master" element={<Protected><AIMaster /></Protected>} />
          <Route path="/replication" element={<Protected><Replication /></Protected>} />
          <Route path="/failover" element={<Protected><Failover /></Protected>} />
          <Route path="/orchestration" element={<Protected><Orchestration /></Protected>} />
          <Route path="/cluster" element={<Protected><ClusterPage /></Protected>} />
          <Route path="/scenarios" element={<Protected><Scenarios /></Protected>} />
          <Route path="/readiness" element={<Protected><Readiness /></Protected>} />
          <Route path="/autoscale" element={<Protected><Autoscale /></Protected>} />
          <Route path="/cdn" element={<Protected><CDN /></Protected>} />
          <Route path="/enforcement" element={<Protected><Enforcement /></Protected>} />
          <Route path="/geo" element={<Protected><Geo /></Protected>} />
          <Route path="/multicloud" element={<Protected><Multicloud /></Protected>} />
          <Route path="/rollout" element={<Protected><Rollout /></Protected>} />
          <Route path="/logical-replication" element={<Protected><LogicalReplication /></Protected>} />
          <Route path="/dr" element={<Protected><DRControl /></Protected>} />
          <Route path="/voting" element={<Protected><Voting /></Protected>} />
          <Route path="/release" element={<Protected><Release /></Protected>} />
          <Route path="/purge" element={<Protected><Purge /></Protected>} />
          <Route path="/webhooks" element={<Protected><WebhookDLQ /></Protected>} />
          <Route path="/soak" element={<Protected><Soak /></Protected>} />
          <Route path="/security" element={<Protected><Security /></Protected>} />
          <Route path="/roles" element={<Protected><Roles /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
